import { Store } from "redux";
import { RootState, store } from "../redux/redux-toolkit-store";
import { Scene } from '../interfaces/scene.interface';
import { postMessageService } from './post-message.service';
import { QuizEvent } from "../enums/quiz-event.enum";
import { back, changeCurrentState, changeScene, forward, pushScene, rollupHistory, setDirection, setScene } from "../redux/history-slice";
import equal from 'deep-equal';
import { SceneToken } from "../enums/scene-token.enum";
import { analyticsService } from "../analytics.service";
import { AnalyticsEvent } from "../enums/analytics-event.enum";
import { pause } from "../utils/pause";
import { BrowserHistoryCommand } from "../enums/browser-history-command.enum";
import { EventEmitter } from './event-emitter.service';
import { QuizMode } from "../enums/quiz-mode.enum";

export enum HistoryServiceEvent {
  BACK = "BACK",
  FORWARD = "FORWARD",
}

export class StackService {
  constructor(private readonly eventEmitter: EventEmitter<"execute">) {}

  public stack: Scene[] = [];

  public onExecute(callback: (scene: Scene) => void) {
    this.eventEmitter.subscribe<Scene>("execute", scene => callback(scene));
  }

  public add(scene: Scene) {
    this.stack.push(scene);
    if (this.stack.length == 1) this.eventEmitter.emit("execute", this.stack[0]);
  }

  public finishAction() {
    this.stack.shift();
    if (this.stack.length) {
      this.eventEmitter.emit("execute", this.stack[0]);
    }
  }
}

export class BrowserHistoryService {
  constructor(
    private readonly quizHistoryService: QuizHistoryService, 
    private readonly stackService: StackService
  ) {
    this.stackService.onExecute(scene => this.onChange(scene));
  }

  public perform(command: BrowserHistoryCommand, scene?: Scene) {
    postMessageService.send({ event: QuizEvent.CHANGE_HISTORY, data: { type: command, scene: scene }  });
  }

  public addToQueue(scene: Scene) {
    this.stackService.add(scene);
  }

  public async onChange(scene: Scene) {
    const { currentScene } = this.quizHistoryService;
    // console.log("change history from plugin-data.hook");
    // console.log("scene from plugin", scene);
    // console.log("current scene", currentScene);

    if (scene) {
      let turnOffCurrentScene = !(currentScene.token == SceneToken.START_PAGE && scene.token == SceneToken.SIGN_UP);
      const backConditions = (scene.token == SceneToken.START_PAGE && currentScene.token == SceneToken.QUESTION) ||
        (scene.token == SceneToken.START_PAGE && currentScene.token == SceneToken.SIGN_UP) ||
        (scene.token == currentScene.token &&
        (scene.subState?.data?.index || 0) < (currentScene.subState?.data?.index || 0)) ||
        (scene.token == SceneToken.QUESTION && currentScene.token == SceneToken.FORM);
  
      const forwardConditions = (scene.token == currentScene.token &&
        (scene.subState?.data?.index || 0) > (currentScene.subState?.data?.index || 0)) ||
        (scene.token == SceneToken.FORM && currentScene.token == SceneToken.QUESTION) || 
        ((scene.token == SceneToken.QUESTION || scene.token == SceneToken.SIGN_UP) && currentScene.token == SceneToken.START_PAGE) ||
        (currentScene.token == SceneToken.START_PAGE && scene.token == SceneToken.SIGN_UP) ||
        (scene.token == SceneToken.FINAL && currentScene.token == SceneToken.FINAL);
      
      const closeConditions = ((scene.token == SceneToken.QUESTION || scene.token === undefined) && (currentScene.token == SceneToken.RESULT || 
        currentScene.token == SceneToken.FINAL));

      if (backConditions) await this.quizHistoryService.back();
      if (forwardConditions) await this.quizHistoryService.forward(turnOffCurrentScene);
  
      if (closeConditions && store.getState().service.mode == QuizMode.POPUP) {
        this.perform(BrowserHistoryCommand.CLOSE);
        this.quizHistoryService.rollupHistory();
        analyticsService.send(AnalyticsEvent.CLOSE);
      }
    } else await this.quizHistoryService.back();

    this.stackService.finishAction();
  }
}

export class QuizHistoryService {
  constructor(private readonly store: Store<RootState>) {}

  private eventEmitter = new EventEmitter<HistoryServiceEvent>();

  private turnOffCurrentScene(newStateToken: SceneToken) {
    if (newStateToken === undefined) return;

    if (newStateToken != this.currentScene?.token) {
      this.store.dispatch(changeCurrentState({
        ...this.currentScene, status: false
      }));
    }

    if (this.currentScene.subState) {
      this.store.dispatch(changeCurrentState({
        ...this.currentScene,
        subState: {
          ...this.currentScene.subState,
          status: false
        }
      }));
    }
  }

  private enableScene(previousScene: Scene) {
    this.store.dispatch(changeScene({
      current: previousScene,
      update: { 
        ...previousScene, 
        status: true,
        subState: previousScene?.subState ? { ...previousScene?.subState, status: true } : undefined }
    }))
  }

  public async push(scene: Scene, turnOffCurrentScene: boolean = true) {
    if (turnOffCurrentScene) {
      this.turnOffCurrentScene(scene.token);
      this.store.dispatch(setDirection(true));
      await pause(600);
    }

    const subState = scene.subState ? { ...scene.subState, status: true } : undefined;
    this.store.dispatch(pushScene({ ...scene, status: true, subState }));
  }

  public async back() {
    this.eventEmitter.emit(HistoryServiceEvent.BACK, { currentScene: this.currentScene, nextScene: this.nextScene, previousScene: this.previousScene });
    this.enableScene(this.previousScene);
    this.turnOffCurrentScene(this.previousScene.token);
    this.store.dispatch(setDirection(false));
    await pause(600);
    this.store.dispatch(back());
  }

  public async forward(turnOffCurrentScene = true) {
    this.eventEmitter.emit(HistoryServiceEvent.FORWARD, { currentScene: this.currentScene, nextScene: this.nextScene });
    this.enableScene(this.nextScene);
    if (turnOffCurrentScene) {
      this.turnOffCurrentScene(this.nextScene?.token);
      this.store.dispatch(setDirection(true));
      await pause(600);
    }

    this.store.dispatch(forward());
  }

  public async replace(scene: Scene) {
    const subState = scene.subState ? { ...scene.subState, status: true } : undefined;
    this.store.dispatch(setScene({ ...scene, status: true, subState }));
  }

  public async rollupHistory() {
    this.store.dispatch(rollupHistory());
  }

  public get currentScene() {
    return this.store.getState().history.current;
  }

  public get lastScene() {
    const stack = this.store.getState().history.stack;
    return stack[stack.length - 1];
  }

  public get nextScene() {
    const stack = this.store.getState().history.stack;
    const current = this.store.getState().history.current;
    let nextScene: Scene | null;
    
    const currentIndex = stack.findIndex(scene => equal(scene, current));
    if (currentIndex != -1) nextScene = stack[currentIndex + 1] || null;
    return nextScene;
  }

  public getCurrentDirection() {
    return this.store.getState().history.direction;
  }

  public getSceneStatus(token: SceneToken) {
    const stack = this.store.getState().history.stack;
    const scene = stack.find(item => item.token == token);
    return scene?.status;
  }

  public get previousScene() {
    const stack = this.store.getState().history.stack;
    const current = this.store.getState().history.current;
    let previousScene: Scene;

    const currentIndex = stack.findIndex(scene => {
      return equal(
        { ...scene, status: undefined, subState: { ...scene.subState, status: undefined }}, 
        { ...current, status: undefined, subState: { ...current.subState, status: undefined }}
      );
    });
    if (currentIndex !== -1) previousScene = stack[currentIndex - 1 || 0];
    return previousScene;
  }

  public subscribe(event: HistoryServiceEvent, callback: (data: { currentScene: Scene, nextScene: Scene, previousScene: Scene }) => void, options?: { once?: boolean }) {
    const sub = this.eventEmitter.subscribe(event, callback);
    if (options?.once) sub.unsubscribeOnEmit();
    return sub;
  }
}

const stackService = new StackService(new EventEmitter());
export const quizHistoryService = new QuizHistoryService(store);
export const browserHistoryService = new BrowserHistoryService(quizHistoryService, stackService);